// Global macOS-style notification manager
interface NotificationData {
  id: string;
  title: string;
  description: string;
}

class MacOSNotificationManager {
  private notifications: NotificationData[] = [];
  private listeners = new Set<(notifications: NotificationData[]) => void>();

  addNotification(title: string, description: string): string {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: NotificationData = {
      id,
      title,
      description
    };

    this.notifications = [...this.notifications, newNotification];
    this.notifyListeners();
    return id;
  }

  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(notification => notification.id !== id);
    this.notifyListeners();
  }

  clearAllNotifications(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  getNotifications(): NotificationData[] {
    return [...this.notifications];
  }

  subscribe(listener: (notifications: NotificationData[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }
}

// Global singleton instance
export const macOSNotificationManager = new MacOSNotificationManager();

// Hook for React components
export const useMacOSNotificationManager = () => {
  return {
    addNotification: macOSNotificationManager.addNotification.bind(macOSNotificationManager),
    removeNotification: macOSNotificationManager.removeNotification.bind(macOSNotificationManager),
    clearAllNotifications: macOSNotificationManager.clearAllNotifications.bind(macOSNotificationManager),
    getNotifications: macOSNotificationManager.getNotifications.bind(macOSNotificationManager),
    subscribe: macOSNotificationManager.subscribe.bind(macOSNotificationManager)
  };
};